import * as yup from 'yup'
import { isJulyMembershipStartAvailable } from '../../../utils/membershipChecks'

const phoneRegEx = /^\+\d[\d\s-]*$/

const personalInfoSchema = yup
  .object({
    lastname: yup.string().required(),
    firstname: yup.string().max(50).required(),
    els_hetu: yup.string().required(),
    nickname: yup.string().max(50).required('Täytä puuttuva kenttä'),
    address: yup.object().shape({
      line1: yup.string().max(250).required('Täytä puuttuva kenttä'),
      line2: yup.string().max(250).optional(),
      line3: yup.string().max(250).optional(),
      postalcode: yup.string().required('Täytä puuttuva kenttä'),
      //sll_tyosuhde_sijaintikunta: yup.string().max(80).required('Täytä puuttuva kenttä'),
      els_postikieli: yup.string().required('Täytä puuttuva kenttä'),
      country: yup.string().required('Täytä puuttuva kenttä'),
      markkinoinninesto: yup
        .string()
        .nullable()
        .transform(value => (value == null ? '' : value))
        .typeError('Valitse yksi vaihtoehto')
        .required('Valitse yksi vaihtoehto')
    }),
    mobilephone: yup
      .string()
      .max(50)
      .matches(phoneRegEx, 'Tarkista puhelinnumeron muoto')
      .required(),
    emailaddress1: yup
      .string()
      .email('Syötä validi sähköpostiosoite')
      .max(100)
      .required('Täytä puuttuva kenttä'),
    cursus: yup.object().shape({
      els_oppilaitos: yup.string().required('Täytä puuttuva kenttä'),
      els_aloitusvuosi: yup.string().required('Täytä puuttuva kenttä'),
      els_lukukausi: yup.string().required('Täytä puuttuva kenttä')
    })
  })
  .required()

const membershipStartsSchema = yup.object().shape({
  membership_starts: yup
    .string()
    .required()
    .test('valid-membership-starts', 'Valitse jäsenyyden aloitusajankohta', value => {
      const allowed = ['WHEN_APPROVED', 'BEGINNING_OF_CALENDAR_YEAR']
      if (isJulyMembershipStartAvailable()) {
        allowed.push('BEGINNING_OF_HALF_YEAR')
      }
      return allowed.includes(value ?? '')
    })
})

const employment1Schema = yup.object().shape({
  // tyosuhdetoimiala: yup.string().required('Täytä puuttuva kenttä'),
  employment: yup
    .object()
    .shape({
      tyosuhdetoimiala: yup.string().required('Valitse toimiala'),
      els_tyosuhdesektori: yup.string().required('Valitse sektori'),
      els_soteorganisaatio: yup.string().required('Valitse työpaikka'), //els_job
      sll_tyosuhde_sijaintikunta: yup.string().required('Valitse työpaikan sijainti'),
      ammattinimike: yup.string().required('Valitse ammattinimike'),
      els_alkupvm: yup
        .string()
        .strict(true)
        .required('Työsuhteen alkupäivä on pakollinen tieto')
        .test('not-null', 'Työsuhteen alkupäivä on pakollinen tieto', function (value) {
          // Explicitly check for null, undefined, and empty string
          if (
            value === null ||
            value === undefined ||
            value === '' ||
            (typeof value === 'string' && value.trim() === '')
          ) {
            return this.createError({ message: 'Työsuhteen alkupäivä on pakollinen tieto' })
          }
          return true
        })
        .test('valid-date-format', 'Päivämäärän tulee olla muodossa YYYY-MM-DD', function (value) {
          if (!value) {
            return this.createError({ message: 'Työsuhteen alkupäivä on pakollinen tieto' })
          }

          // Check if it's a valid date string format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/
          if (!dateRegex.test(value)) {
            return this.createError({ message: 'Päivämäärän tulee olla muodossa YYYY-MM-DD' })
          }
          return true
        })
        .test('valid-date', 'Virheellinen päivämäärä', function (value) {
          if (!value) {
            return this.createError({ message: 'Työsuhteen alkupäivä on pakollinen tieto' })
          }

          const selectedDate = new Date(value)
          if (isNaN(selectedDate.getTime())) {
            return this.createError({ message: 'Virheellinen päivämäärä' })
          }

          // Additional check: make sure the date components match what was input
          const [year, month, day] = value.split('-').map(Number)
          if (
            selectedDate.getFullYear() !== year ||
            selectedDate.getMonth() !== month - 1 ||
            selectedDate.getDate() !== day
          ) {
            return this.createError({ message: 'Virheellinen päivämäärä' })
          }

          return true
        }),
      els_loppupvm: yup
        .string()
        .nullable()
        .when('tyosuhdeluonne', {
          is: 'maaraaikainen',
          then: schema => schema.required('Määräaikaisen työsuhteen loppupäivä on pakollinen')
        })
        .test(
          'end-after-start',
          'Työsuhteen loppupäivän tulee olla alkupäivän jälkeen',
          function (end) {
            const start = this.parent?.els_alkupvm
            if (!start || !end) {
              return true
            }
            const re = /^\d{4}-\d{2}-\d{2}$/
            if (!re.test(start) || !re.test(end)) {
              return true
            }
            return end > start
          }
        )
    })
    .required()
})

const employment2Schema = yup.object().shape({
  employment: yup.object().shape({
    els_paatoimi: yup.string().required('Täytä puuttuva kenttä'),
    ammattinimike: yup.string().required('Täytä puuttuva kenttä'),
    els_alkupvm: yup.string().required('Täytä puuttuva kenttä')
  })
})

/** Empty schema since Summary page doesn't have any fields */
const summarySchema = yup.object().shape({})

export const membershipFormSchema = [
  // page 0: Personal Info
  personalInfoSchema,
  // page 1: Membership starts
  membershipStartsSchema,
  // page 2: Employment
  employment1Schema,
  // page 3: Summary (no validation needed)
  summarySchema
]
